import React from 'react';
import { motion } from 'framer-motion';
import { DollarSignIcon, ClockIcon, TrendingDownIcon, HeartPulseIcon } from 'lucide-react';
const fadeUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  }
};
interface BenefitCard {
  icon: React.ElementType;
  stat: string;
  title: string;
  description: string;
  iconColor: string;
  iconBg: string;
}
const benefits: BenefitCard[] = [{
  icon: DollarSignIcon,
  stat: 'Thousands per year',
  title: 'Money Saved',
  description: 'Reduce labor costs by eliminating manual verification work and reallocating staff time to higher-value activities.',
  iconColor: 'text-emerald-600',
  iconBg: 'bg-emerald-50'
}, {
  icon: ClockIcon,
  stat: '2-3 hrs/day',
  title: 'Time Saved',
  description: 'Automate eligibility checks from 8-15 minutes down to 10-30 seconds per patient, freeing up staff for patient care.',
  iconColor: 'text-blue-600',
  iconBg: 'bg-blue-50'
}, {
  icon: TrendingDownIcon,
  stat: '~50% lower',
  title: 'Admin Workload',
  description: 'Catch eligibility issues before they become claim denials, reducing rework and administrative burden on your team.',
  iconColor: 'text-purple-600',
  iconBg: 'bg-purple-50'
}, {
  icon: HeartPulseIcon,
  stat: '98-99% accuracy',
  title: 'Positive Impact',
  description: 'Improve treatment outcomes and revenue with accurate, real-time coverage data that supports better patient care decisions.',
  iconColor: 'text-rose-600',
  iconBg: 'bg-rose-50'
}];
export function BenefitsSection() {
  return <section className="py-20 lg:py-28 bg-white relative overflow-hidden" data-id="element-18">
      {/* Real healthcare photo background */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-19">
        <img src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=1920&q=80" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-[0.05]" data-id="element-20" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-21">
        {/* Section Header */}
        <div className="text-center mb-14" data-id="element-22">
          <motion.h2 {...fadeUp} className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-23">
            Better Verification. Better Outcomes.
          </motion.h2>
          <motion.p {...fadeUp} transition={{
          delay: 0.1
        }} className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-24">
            Stop wasting hours on manual eligibility checks. Get instant,
            accurate verification every time.
          </motion.p>
        </div>

        {/* 4 Equal Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10" data-id="element-25">
          {benefits.map((benefit, i) => <motion.div key={benefit.title} {...fadeUp} transition={{
          delay: 0.15 + i * 0.08
        }} className="bg-white border border-slate-200 rounded-2xl p-8 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow" data-id="element-26">
              <div className={`w-14 h-14 rounded-2xl ${benefit.iconBg} flex items-center justify-center mb-5`} data-id="element-27">
                <benefit.icon className={`w-7 h-7 ${benefit.iconColor}`} data-id="element-28" />
              </div>

              <div className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" data-id="element-29">
                {benefit.stat}
              </div>

              <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-30">
                {benefit.title}
              </h3>

              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-31">
                {benefit.description}
              </p>
            </motion.div>)}
        </div>

        {/* Source Citation */}
        <motion.div {...fadeUp} transition={{
        delay: 0.5
      }} className="text-center" data-id="element-32">
          <p className="text-xs text-slate-500" data-id="element-33">
            Data from MBW RCM, Roving Health, Experian Health, CAQH (2023-2024)
          </p>
        </motion.div>
      </div>
    </section>;
}